import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react'
import mixpanel from 'mixpanel-browser'
import {
  getCurrentUserSession,
  authFetch,
  logoutUser as logoutCognitoUser,
} from '../utils/cognito'
import { useUserStore } from '../../store/user-store'

type AuthContextType = {
  isAuthenticated: boolean
  setIsAuthenticated: React.Dispatch<React.SetStateAction<boolean>>
  isOnboardingComplete: boolean
  setIsOnboardingComplete: React.Dispatch<React.SetStateAction<boolean>>
  logoutUser: () => Promise<any>
  isLoading: boolean
  fetchOnboardingStatus: () => Promise<boolean>
}

export const AuthContext = createContext<AuthContextType | null>(null)

type AuthProviderProps = {
  children: ReactNode
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const { fetchUser } = useUserStore()
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isOnboardingComplete, setIsOnboardingComplete] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  const logoutUser = async () => {
    try {
      await logoutCognitoUser()
      setIsAuthenticated(false)
      setIsOnboardingComplete(false)
      mixpanel.reset()
    } catch (error) {
      console.error(error)
    }
  }

  const fetchOnboardingStatus = async () => {
    const response = await authFetch(
      `${process.env.REACT_APP_API_URL}/user-onboarding`,
    )

    const { complete } = await response.json()
    setIsOnboardingComplete(complete)
    return Boolean(complete)
  }

  useEffect(() => {
    const checkUserSession = async () => {
      try {
        await getCurrentUserSession()
        setIsAuthenticated(true)
        await fetchUser()
        await fetchOnboardingStatus()
      } catch (error) {
        setIsAuthenticated(false)
      } finally {
        setIsLoading(false)
      }
    }

    checkUserSession()
  }, [fetchUser])

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        setIsAuthenticated,
        isOnboardingComplete,
        setIsOnboardingComplete,
        fetchOnboardingStatus,
        logoutUser,
        isLoading,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
